import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Link, useSearchParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChartPie, faChartColumn, faMagnifyingGlassChart } from '@fortawesome/free-solid-svg-icons';
import { API_BASE_URL } from '../../config/api';
import StoreDashboardCustomView from './StoreDashboardCustomView';
import GraphBuilderPanel from '../analytics/GraphBuilderPanel';

type HomeTab = 'overview' | 'custom' | 'inspect';

const tabButtonClass = (active: boolean) => `nav-link${active ? ' active' : ''}`;
const TAB_STYLE: React.CSSProperties = { fontSize: '0.9rem', padding: '0.4rem 0.8rem' };

interface OverviewMetric {
  configured: boolean;
  error: string | null;
}

interface OverviewData {
  sales: OverviewMetric & {
    revenue: number | null;
    orders: number | null;
    spend: number | null;
    roas: number | null;
    adsConfigured: boolean;
    days: number;
  };
  fleet: OverviewMetric & { percent: number | null; onLatest: number; devices: number; latestVersion: string | null };
  work: OverviewMetric & { percent: number | null; completed: number; open: number; started: number; teams: number };
  support: OverviewMetric & { open: number | null };
  social: OverviewMetric & {
    followers: number;
    followersGained: number;
    followersLost: number;
    views: number;
    videos: number;
  };
}

const money = (value?: number | null) => {
  if (value == null || Number.isNaN(value)) return '—';
  return `$${value.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
};

const count = (value?: number | null) => {
  if (value == null || Number.isNaN(value)) return '—';
  return value.toLocaleString();
};

const percent = (value?: number | null) => {
  if (value == null || Number.isNaN(value)) return '—';
  return `${value % 1 === 0 ? value.toFixed(0) : value.toFixed(1)}%`;
};

const ratio = (value?: number | null) => {
  if (value == null || Number.isNaN(value)) return '—';
  return `${value.toFixed(1)}x`;
};

const formatVersion = (version?: string | null) => {
  if (!version) return '—';
  return version.match(/^[vV]/) ? version : `v${version}`;
};

const Widget: React.FC<{
  to: string;
  label: string;
  value: string;
  detail: string;
}> = ({ to, label, value, detail }) => (
  <Link to={to} className="store-dash-card is-link">
    <span>{label}</span>
    <strong>{value}</strong>
    <small>{detail}</small>
  </Link>
);

const Section: React.FC<{
  title: string;
  to: string;
  link: string;
  children: React.ReactNode;
}> = ({ title, to, link, children }) => (
  <section className="store-dash-overview-section">
    <div className="store-dash-chart-head">
      <h2>{title}</h2>
      <Link to={to}>{link}</Link>
    </div>
    <div className="store-dash-cards store-dash-overview-cards">
      {children}
    </div>
  </section>
);

const StoreDashboardOverviewView: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const activeTab: HomeTab = tabParam === 'custom' ? 'custom' : tabParam === 'inspect' ? 'inspect' : 'overview';
  const [data, setData] = useState<OverviewData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const selectTab = (tab: HomeTab) => {
    setSearchParams(tab === 'overview' ? {} : { tab }, { replace: true });
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/store-dashboard/overview`, { withCredentials: true });
      setData(response.data);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load overview');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const needsApis = data && (
    !data.sales.configured
    || !data.sales.adsConfigured
    || !data.work.configured
    || !data.social.configured
  );

  return (
    <div className="store-dash">
      <div className="store-dash-toolbar">
        <div>
          <div className="store-dash-kicker">Fluora</div>
          <h1>Home</h1>
        </div>
      </div>

      <ul className="nav nav-tabs nav-tabs-sm flex-nowrap overflow-auto mb-4" style={{ borderBottom: '1px solid #dee2e6' }}>
        <li className="nav-item">
          <button type="button" className={tabButtonClass(activeTab === 'overview')} style={TAB_STYLE} onClick={() => selectTab('overview')}>
            <FontAwesomeIcon icon={faChartPie} className="me-1" />
            Overview
          </button>
        </li>
        <li className="nav-item">
          <button type="button" className={tabButtonClass(activeTab === 'custom')} style={TAB_STYLE} onClick={() => selectTab('custom')}>
            <FontAwesomeIcon icon={faChartColumn} className="me-1" />
            Custom
          </button>
        </li>
        <li className="nav-item">
          <button type="button" className={tabButtonClass(activeTab === 'inspect')} style={TAB_STYLE} onClick={() => selectTab('inspect')}>
            <FontAwesomeIcon icon={faMagnifyingGlassChart} className="me-1" />
            Inspect
          </button>
        </li>
      </ul>

      {activeTab === 'custom' ? (
        <StoreDashboardCustomView />
      ) : activeTab === 'inspect' ? (
        <GraphBuilderPanel onSaved={() => selectTab('custom')} saveLabel="Save Graph" />
      ) : (
        <OverviewTabBody data={data} loading={loading} error={error} needsApis={needsApis} />
      )}
    </div>
  );
};

interface OverviewTabBodyProps {
  data: OverviewData | null;
  loading: boolean;
  error: string | null;
  needsApis: boolean | null;
}

const OverviewTabBody: React.FC<OverviewTabBodyProps> = ({ data, loading, error, needsApis }) => (
  <>
      {needsApis && (
        <p className="store-dash-hint">
          Some sources are still off. Connect them on the <Link to="/apis">APIs</Link> page.
        </p>
      )}

      {loading ? (
        <div className="d-flex justify-content-center py-5">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : error ? (
        <div className="alert alert-danger">{error}</div>
      ) : (
        <>
          <Section title="Financial" to="/sales" link="Sales">
            <Widget
              to="/sales"
              label="Revenue"
              value={data?.sales.configured ? money(data.sales.revenue) : '—'}
              detail={data?.sales.error || (data?.sales.configured ? `Last ${data.sales.days} days` : 'Connect Shopify')}
            />
            <Widget
              to="/sales"
              label="Orders"
              value={data?.sales.configured ? count(data.sales.orders) : '—'}
              detail={data?.sales.configured ? 'Shopify' : 'Connect Shopify'}
            />
            <Widget
              to="/marketing"
              label="Ad spend"
              value={data?.sales.adsConfigured ? money(data.sales.spend) : '—'}
              detail={data?.sales.adsConfigured ? 'Meta + Google' : 'Connect Meta or Google'}
            />
            <Widget
              to="/marketing"
              label="ROAS"
              value={data?.sales.adsConfigured ? ratio(data.sales.roas) : '—'}
              detail={data?.sales.adsConfigured ? 'Revenue / spend' : 'Needs ads + Shopify'}
            />
          </Section>

          <Section title="Work" to="/work" link="Linear">
            <Widget
              to="/work"
              label="Open"
              value={data?.work.configured ? count(data.work.open) : '—'}
              detail={data?.work.error || (data?.work.configured ? 'Backlog + in progress' : 'Connect Linear')}
            />
            <Widget
              to="/work"
              label="In progress"
              value={data?.work.configured ? count(data.work.started) : '—'}
              detail={data?.work.configured ? 'Started issues' : 'Connect Linear'}
            />
            <Widget
              to="/work"
              label="Done"
              value={data?.work.configured ? count(data.work.completed) : '—'}
              detail={data?.work.configured ? 'Last 30 days' : 'Connect Linear'}
            />
            <Widget
              to="/work"
              label="Teams"
              value={data?.work.configured ? count(data.work.teams) : '—'}
              detail={data?.work.configured ? 'Linear teams' : 'Connect Linear'}
            />
          </Section>

          <Section title="Fleet" to="/fleet" link="Devices">
            <Widget
              to="/fleet"
              label="Devices"
              value={data?.fleet.devices ? count(data.fleet.devices) : '—'}
              detail={data?.fleet.error || (data?.fleet.devices ? 'Official models' : 'No fleet data')}
            />
            <Widget
              to="/fleet"
              label="On latest"
              value={percent(data?.fleet.percent)}
              detail={data?.fleet.devices
                ? `${count(data.fleet.onLatest)} of ${count(data.fleet.devices)}`
                : 'No fleet data'}
            />
            <Widget
              to="/fleet"
              label="Latest"
              value={formatVersion(data?.fleet.latestVersion)}
              detail={data?.fleet.latestVersion ? 'Most common production' : 'No production release'}
            />
          </Section>

          <Section title="Social" to="/social" link="Channels">
            <Widget
              to="/social"
              label="Followers"
              value={data?.social.configured ? count(data.social.followers) : '—'}
              detail={data?.social.error || (data?.social.configured ? 'All connected channels' : 'Connect social APIs')}
            />
            <Widget
              to="/social"
              label="New"
              value={data?.social.configured ? count(data.social.followersGained) : '—'}
              detail={data?.social.configured ? 'Last 30 days' : 'Connect social APIs'}
            />
            <Widget
              to="/social"
              label="Lost"
              value={data?.social.configured ? count(data.social.followersLost) : '—'}
              detail={data?.social.configured ? 'Last 30 days' : 'Connect social APIs'}
            />
            <Widget
              to="/social"
              label="Views"
              value={data?.social.configured ? count(data.social.views) : '—'}
              detail={data?.social.configured ? `${count(data.social.videos)} videos` : 'Connect social APIs'}
            />
          </Section>
        </>
      )}
  </>
);

export default StoreDashboardOverviewView;
